import { EVENTS, GOLDEN_EFFECTS, RAINBOW_EFFECTS, VOUCHER_DISCOUNT, VOUCHER_MAX } from '../data/balance';
import { addBuff } from './buffs';
import { spawnStormEggs } from './critters';
import { recordEggsCollected, type ActionResult } from './actions';
import { earn, henStats, nestCapacity, payoutBasis } from './economy';
import { globalAdd, globalMul } from './modifiers';
import { chance, pickWeighted, randInt, randRange } from './rng';
import { bloomIntervalSec, rainbowSpawnMult } from './specials';
import { countHens, countRoosters, emit, findFlock, logActivity, nextId } from './state';
import type { EventKind, GameEvent, GameState, GoldenEffect, RainbowEffect, SimCtx } from './types';

/**
 * Special eggs on the canvas. Spawn chance is a Poisson process with the
 * mean intervals in data/balance.ts. Payouts are "seconds of steady
 * production" so they scale with the farm without compounding buffs.
 */

const money = (n: number) => `$${Math.floor(n).toLocaleString('en-US')}`;

function lifeSec(kind: EventKind): number {
  switch (kind) {
    case 'yolkBurst': return EVENTS.yolkBurst.lifeSec;
    case 'eggShower': return EVENTS.eggShower.lifeSec;
    case 'doubleTime': return EVENTS.doubleTime.lifeSec;
    case 'silver': return EVENTS.silver.lifeSec;
    case 'golden': return EVENTS.golden.lifeSec;
    case 'rotten': return EVENTS.rotten.salvageSec;
    case 'rainbow': return EVENTS.rainbow.lifeSec;
    case 'roosterEgg': return EVENTS.roosterEgg.lifeSec;
    case 'broody': return 15 * 60;
    case 'broodEgg': return Infinity;
  }
}

export function eventLifetimeMs(s: GameState, ctx: SimCtx, kind: EventKind): number {
  let sec = lifeSec(kind);
  if (kind === 'golden') sec += globalAdd(s, ctx, 'goldenWindow');
  if (kind === 'silver') sec += globalAdd(s, ctx, 'silverWindow');
  if (kind === 'rotten') sec += globalAdd(s, ctx, 'salvageWindow');
  const clickable = kind !== 'broody' && kind !== 'broodEgg';
  return sec * 1000 * (clickable ? globalMul(s, ctx, 'eventDuration') : 1);
}

function effectMs(s: GameState, ctx: SimCtx, sec: number): number {
  return sec * 1000 * globalMul(s, ctx, 'eventEffect');
}

/** Picks a free spot in normalized field coordinates, spaced from other events. */
function findSpot(s: GameState, flockId: number): { x: number; y: number } {
  const others = s.events.filter((e) => e.flockId === flockId);
  let best = { x: 0.5, y: 0.5 };
  for (let attempt = 0; attempt < 20; attempt++) {
    const x = randRange(s, 0.08, 0.92);
    const y = randRange(s, 0.12, 0.9);
    best = { x, y };
    if (others.every((o) => Math.hypot(o.x - x, o.y - y) > 0.12)) break;
  }
  return best;
}

export function spawnEvent(s: GameState, ctx: SimCtx, kind: EventKind, flockId: number, extra: Partial<GameEvent> = {}): GameEvent {
  const spot = findSpot(s, flockId);
  const ev: GameEvent = {
    id: nextId(s),
    kind,
    flockId,
    x: spot.x,
    y: spot.y,
    spawnedAt: s.time,
    expiresAt: s.time + eventLifetimeMs(s, ctx, kind),
    ...extra,
  };
  if (kind === 'golden' && !ev.golden) {
    ev.golden = pickWeighted<GoldenEffect>(s, [
      ['burst', GOLDEN_EFFECTS.burst.weight],
      ['layingFrenzy', GOLDEN_EFFECTS.layingFrenzy.weight],
      ['luckyClutch', GOLDEN_EFFECTS.luckyClutch.weight],
      ['clickStorm', GOLDEN_EFFECTS.clickStorm.weight],
    ]);
  }
  if (kind === 'rainbow' && !ev.rainbow) {
    ev.rainbow = pickWeighted<RainbowEffect>(s, [['nestFill', 1], ['harmony', 1], ['prismatic', 1], ['rush', 1]]);
  }
  s.events.push(ev);
  if (kind !== 'broodEgg' && kind !== 'broody') s.timers.lastAnySpawnAt = s.time;
  return ev;
}

const has = (s: GameState, kind: EventKind) => s.events.some((e) => e.kind === kind);
const count = (s: GameState, kind: EventKind) => s.events.filter((e) => e.kind === kind).length;

export function anyFrenzy(s: GameState): boolean {
  if (s.buffs.lay.some((b) => b.until > s.time)) return true;
  return s.flocks.some((f) => f.crowBuffs.some((b) => b.until > s.time));
}

function viewFlockId(s: GameState): number {
  return findFlock(s, s.viewFlock)?.id ?? s.flocks[0]!.id;
}

/** Expiry + spawn rolls for one tick. */
export function tickEvents(s: GameState, ctx: SimCtx, dtMs: number): void {
  const now = s.time;
  // 1) Expire.
  for (let i = s.events.length - 1; i >= 0; i--) {
    const e = s.events[i]!;
    if (e.kind === 'broody' || e.kind === 'broodEgg') continue;
    if (e.kind === 'roosterEgg' && e.incubateUntil) {
      if (now >= e.incubateUntil) {
        s.events.splice(i, 1);
        hatchCockerel(s);
      }
      continue;
    }
    if (now >= e.expiresAt) {
      s.events.splice(i, 1);
      if (e.kind === 'rotten') {
        s.debuffUntil = now + EVENTS.rotten.debuffSec * 1000;
        s.stats.rottenExpired++;
        s.stats.rottenSeen++;
        emit(s, { kind: 'toast', text: 'Phew. A rotten egg went bad — egg value −15% for 30s.', tone: 'warn' });
      }
    }
  }

  const view = viewFlockId(s);
  const t = s.timers;

  // 2) Anti-dead-zone gates.
  if (!t.firstEventDone && s.lifetimeEarned === 0 && now >= EVENTS.firstEventDelaySec * 1000) {
    spawnEvent(s, ctx, 'yolkBurst', view);
    t.firstEventDone = true;
  }
  if (now - t.lastAnySpawnAt >= EVENTS.cadenceFloorSec * 1000) spawnEvent(s, ctx, 'yolkBurst', view);
  const hens = countHens(s);
  if (hens !== t.stuckHens) {
    t.stuckHens = hens;
    t.stuckSince = now;
  } else if (now - t.stuckSince >= EVENTS.stuckWindowSec * 1000 && now - t.stuckBurstAt >= EVENTS.stuckWindowSec * 1000) {
    spawnEvent(s, ctx, 'yolkBurst', view);
    spawnEvent(s, ctx, 'eggShower', view);
    spawnEvent(s, ctx, 'yolkBurst', view);
    t.stuckBurstAt = now;
  }

  // 3) Random spawns.
  const life = s.lifetimeEarned;
  if (!has(s, 'golden') && life >= EVENTS.golden.minLifetime && chance(s, dtMs, EVENTS.golden.meanSec / globalMul(s, ctx, 'goldenSpawn'))) {
    spawnEvent(s, ctx, 'golden', view);
  }
  if (!has(s, 'silver') && life >= EVENTS.silver.minLifetime && chance(s, dtMs, EVENTS.silver.meanSec / globalMul(s, ctx, 'silverSpawn'))) {
    spawnEvent(s, ctx, 'silver', view);
  }
  if (!has(s, 'rotten') && !anyFrenzy(s) && chance(s, dtMs, EVENTS.rotten.meanSec)) spawnEvent(s, ctx, 'rotten', view);
  if (count(s, 'yolkBurst') < EVENTS.yolkBurst.max && chance(s, dtMs, EVENTS.yolkBurst.meanSec)) spawnEvent(s, ctx, 'yolkBurst', view);
  if (!has(s, 'eggShower') && life >= EVENTS.eggShower.minLifetime && chance(s, dtMs, EVENTS.eggShower.meanSec)) spawnEvent(s, ctx, 'eggShower', view);
  if (!has(s, 'doubleTime') && life >= EVENTS.doubleTime.minLifetime && chance(s, dtMs, EVENTS.doubleTime.meanSec)) spawnEvent(s, ctx, 'doubleTime', view);
  const rainbow = rainbowSpawnMult(s);
  if (rainbow > 0 && !has(s, 'rainbow') && chance(s, dtMs, EVENTS.rainbow.meanSec / rainbow)) spawnEvent(s, ctx, 'rainbow', view);
  const roosters = countRoosters(s);
  if (roosters >= 1 && roosters < 10 && s.vouchers < VOUCHER_MAX && !has(s, 'roosterEgg') && chance(s, dtMs, EVENTS.roosterEgg.meanSec)) {
    spawnEvent(s, ctx, 'roosterEgg', view);
  }
  // Mille Fleur blooms (per flock, on its own flock).
  for (const f of s.flocks) {
    const every = bloomIntervalSec(f, EVENTS.bloom.intervalSec, EVENTS.bloom.pairedIntervalSec);
    if (every === null) { f.bloomNextAt = null; continue; }
    if (f.bloomNextAt === null) f.bloomNextAt = now + every * 1000;
    else if (now >= f.bloomNextAt) {
      spawnEvent(s, ctx, 'yolkBurst', f.id, { bloom: true, expiresAt: now + EVENTS.bloom.lifeSec * 1000 });
      f.bloomNextAt = now + every * 1000;
    }
  }
}

function hatchCockerel(s: GameState): void {
  s.vouchers = Math.min(VOUCHER_MAX, s.vouchers + 1);
  s.stats.roosterEggsHatched++;
  const pct = Math.round(Math.min(VOUCHER_MAX, s.vouchers) * VOUCHER_DISCOUNT * 100);
  emit(s, { kind: 'roosterHatched', discountPct: pct });
  logActivity(s, 'event', 'Cockerel hatched', `Next rooster −${pct}%`);
}

// ── Collection ────────────────────────────────────────────────────────

function collectAllNests(s: GameState, ctx: SimCtx, mult: number): { value: number; eggs: number } {
  let value = 0;
  let eggs = 0;
  for (const f of s.flocks) {
    for (const b of f.boxes) {
      if (!b.hen || b.eggs <= 0) continue;
      const st = henStats(s, ctx, f, b.hen);
      value += st.eggValue * mult * b.eggs;
      eggs += b.eggs;
      b.eggs = 0;
      b.oldestEggAt = null;
    }
  }
  return { value, eggs };
}

/** Player clicked an event. Broody events are handled by broody.ts (choice UI). */
export function collectEvent(s: GameState, ctx: SimCtx, eventId: number): ActionResult {
  const idx = s.events.findIndex((e) => e.id === eventId);
  const e = s.events[idx];
  if (!e) return { ok: false, reason: 'Gone' };
  if (e.kind === 'broody') return { ok: false, reason: 'choose' };
  const basis = payoutBasis(s, ctx);
  const remove = () => { s.events.splice(s.events.indexOf(e), 1); };
  let amount = 0;
  let text = '';
  switch (e.kind) {
    case 'yolkBurst': {
      const r = collectAllNests(s, ctx, 2);
      amount = earn(s, r.value);
      recordEggsCollected(s, r.eggs);
      text = e.bloom ? `Bloom! ${r.eggs} eggs at 2× — ${money(amount)}` : `Yolk Burst! ${r.eggs} eggs at 2× — ${money(amount)}`;
      remove();
      break;
    }
    case 'eggShower': {
      const n = randInt(s, EVENTS.eggShower.eggsMin, EVENTS.eggShower.eggsMax);
      spawnStormEggs(s, e.flockId, n);
      text = `Egg Shower! ${n} eggs are raining down — grab them!`;
      remove();
      break;
    }
    case 'doubleTime': {
      addBuff(s.buffs.lay, EVENTS.doubleTime.layMult, effectMs(s, ctx, EVENTS.doubleTime.durationSec), s.time, 'doubleTime');
      text = `Double Time! Hens lay 2× faster for ${EVENTS.doubleTime.durationSec}s.`;
      remove();
      break;
    }
    case 'silver': {
      s.stats.silverClicked++;
      const golden = s.events.find((x) => x.kind === 'golden');
      const payoutMul = globalMul(s, ctx, 'silverPayout');
      if (golden) {
        s.events.splice(s.events.indexOf(golden), 1);
        amount = earn(s, basis * EVENTS.silver.stormPayoutSec * payoutMul);
        addBuff(s.buffs.auto, EVENTS.silver.stormGatherMult, effectMs(s, ctx, EVENTS.silver.stormDurationSec), s.time, 'silverStorm');
        text = `SILVER STORM! ${money(amount)} + gathered eggs worth 3× for ${EVENTS.silver.stormDurationSec}s!`;
      } else {
        amount = earn(s, basis * EVENTS.silver.payoutSec * payoutMul);
        text = `Silver egg! +${money(amount)}`;
      }
      remove();
      break;
    }
    case 'golden': {
      s.stats.goldenClicked++;
      switch (e.golden ?? 'burst') {
        case 'burst':
          amount = earn(s, basis * GOLDEN_EFFECTS.burst.payoutSec);
          text = `Egg Burst! +${money(amount)}`;
          break;
        case 'layingFrenzy':
          addBuff(s.buffs.lay, GOLDEN_EFFECTS.layingFrenzy.layMult, effectMs(s, ctx, GOLDEN_EFFECTS.layingFrenzy.durationSec), s.time, 'golden:layingFrenzy');
          text = `Laying Frenzy! 3× laying for ${GOLDEN_EFFECTS.layingFrenzy.durationSec}s.`;
          break;
        case 'luckyClutch': {
          const mult = 1 + (GOLDEN_EFFECTS.luckyClutch.mult - 1) * globalMul(s, ctx, 'clutchPower');
          s.clutch.push({ mult, remaining: GOLDEN_EFFECTS.luckyClutch.eggs, source: 'golden:luckyClutch' });
          text = `Lucky Clutch! Your next ${GOLDEN_EFFECTS.luckyClutch.eggs} clicked eggs are worth ${mult.toFixed(0)}×.`;
          break;
        }
        case 'clickStorm': {
          const ms = effectMs(s, ctx, GOLDEN_EFFECTS.clickStorm.durationSec);
          addBuff(s.buffs.click, GOLDEN_EFFECTS.clickStorm.clickMult, ms, s.time, 'golden:clickStorm');
          addBuff(s.buffs.lay, GOLDEN_EFFECTS.clickStorm.layMult, ms, s.time, 'golden:clickStorm');
          text = `Egg Storm! Clicks worth 5× and storm eggs for ${GOLDEN_EFFECTS.clickStorm.durationSec}s!`;
          break;
        }
      }
      remove();
      break;
    }
    case 'rainbow': {
      s.stats.rainbowClicked++;
      switch (e.rainbow ?? 'nestFill') {
        case 'nestFill': {
          let filled = 0;
          for (const f of s.flocks) for (const b of f.boxes) {
            if (!b.hen || b.hen.broodySince !== null) continue;
            const cap = nestCapacity(b);
            filled += Math.max(0, cap - b.eggs);
            if (b.eggs < cap) { b.eggs = cap; b.oldestEggAt ??= s.time; }
          }
          text = `Rainbow Nests! ${filled} fresh eggs appeared.`;
          break;
        }
        case 'harmony': {
          const ms = effectMs(s, ctx, RAINBOW_EFFECTS.harmony.durationSec);
          addBuff(s.buffs.lay, RAINBOW_EFFECTS.harmony.layMult, ms, s.time, 'rainbow:harmony');
          addBuff(s.buffs.auto, RAINBOW_EFFECTS.harmony.autoMult, ms, s.time, 'rainbow:harmony');
          text = `Rainbow Harmony! 3× laying and 3× gathered egg value for ${RAINBOW_EFFECTS.harmony.durationSec}s.`;
          break;
        }
        case 'prismatic': {
          const mult = 1 + (RAINBOW_EFFECTS.prismatic.mult - 1) * globalMul(s, ctx, 'clutchPower');
          s.clutch.push({ mult, remaining: RAINBOW_EFFECTS.prismatic.eggs, source: 'rainbow:prismatic' });
          text = `Prismatic! Your next ${RAINBOW_EFFECTS.prismatic.eggs} clicked eggs are worth ${mult.toFixed(0)}×.`;
          break;
        }
        case 'rush': {
          const ms = effectMs(s, ctx, RAINBOW_EFFECTS.rush.durationSec);
          addBuff(s.buffs.gather, RAINBOW_EFFECTS.rush.gatherMult, ms, s.time, 'rainbow:rush');
          addBuff(s.buffs.click, RAINBOW_EFFECTS.rush.clickMult, ms, s.time, 'rainbow:rush');
          text = `Rainbow Rush! 3× gathering and 5× clicks for ${RAINBOW_EFFECTS.rush.durationSec}s.`;
          break;
        }
      }
      remove();
      break;
    }
    case 'rotten': {
      s.stats.rottenSeen++;
      s.stats.rottenSalvaged++;
      amount = earn(s, Math.max(EVENTS.rotten.salvageFloor, basis * EVENTS.rotten.salvagePayoutSec));
      text = `Salvaged a rotten egg for compost: +${money(amount)}`;
      remove();
      break;
    }
    case 'roosterEgg': {
      if (e.incubateUntil) return { ok: false, reason: 'Incubating' };
      e.incubateUntil = s.time + randRange(s, EVENTS.roosterEgg.incubateMinSec, EVENTS.roosterEgg.incubateMaxSec) * 1000;
      e.expiresAt = Infinity;
      text = 'Rooster egg! Keeping it warm — it will hatch in a few minutes.';
      break;
    }
    case 'broodEgg': {
      amount = earn(s, e.reward ?? 0);
      text = `Brood egg! +${money(amount)}`;
      remove();
      break;
    }
  }
  emit(s, { kind: 'eventCollected', eventKind: e.kind, text, amount, x: e.x, y: e.y, flockId: e.flockId });
  if (e.kind === 'golden' || e.kind === 'silver' || e.kind === 'rainbow') logActivity(s, 'bonus', text.split('!')[0] + '!', amount ? money(amount) : '');
  return { ok: true, amount, detail: text };
}

/** Debug/testing helper: seconds until an event of this kind is allowed by lifetime gates. */
export function eventGateMet(s: GameState, kind: EventKind): boolean {
  switch (kind) {
    case 'golden': return s.lifetimeEarned >= EVENTS.golden.minLifetime;
    case 'silver': return s.lifetimeEarned >= EVENTS.silver.minLifetime;
    case 'eggShower': return s.lifetimeEarned >= EVENTS.eggShower.minLifetime;
    case 'doubleTime': return s.lifetimeEarned >= EVENTS.doubleTime.minLifetime;
    default: return true;
  }
}

